import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import {
  ACCURACY_STARE_MS,
  CALIBRATION_POINTS,
  CLICKS_PER_POINT,
  MIN_CALIBRATION_ACCURACY,
  applyAccuracyResult,
  canStartExam,
  createInitialCalibrationState,
  isCenterVisible,
  pointOpacity,
  recordPointClick,
  shouldStartAccuracy,
  type CalibrationPointId,
  type CalibrationUiState,
} from '@/monitoring/webgazerCalibrationState';
import {
  clearPlottingCanvas,
  clearWebGazerData,
  ensurePlottingCanvas,
  loadWebGazer,
  measureCalibrationAccuracy,
  removePlottingCanvas,
  setCalibrationPreview,
  startWebGazer,
  stopWebGazer,
} from '@/monitoring/webgazerMonitor';
import { useMonitoringStore } from '@/stores/monitoringStore';

interface WebGazerCalibrationProps {
  open: boolean;
  onComplete: () => void;
  onCancel?: () => void;
}

/**
 * Official WebGazer-style calibration: 5 clicks per point, center last, then 5s stare accuracy.
 */
export function WebGazerCalibration({ open, onComplete, onCancel }: WebGazerCalibrationProps) {
  const { updateCalibration, setError } = useMonitoringStore();
  const [ui, setUi] = useState<CalibrationUiState>(createInitialCalibrationState);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const measureStartedRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    setUi(createInitialCalibrationState());
    setStarted(false);
    setLocalError(null);
    setHelpOpen(false);
    setMeasuring(false);
    measureStartedRef.current = false;

    updateCalibration({
      status: 'calibrating',
      progress: 0,
      message: 'Prepare for gaze calibration. Always follow the mouse with your eyes.',
      canStartExam: false,
    });

    let cancelled = false;
    (async () => {
      setStarting(true);
      try {
        await loadWebGazer();
        if (cancelled) return;
        await startWebGazer(() => {
          /* predictions unused during calibration — WebGazer learns from clicks */
        });
        if (cancelled) return;
        setCalibrationPreview(true);
        setStarted(true);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Could not start on-device gaze (WebGazer).';
        setLocalError(message);
        setError(message);
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, updateCalibration, setError]);

  useEffect(() => {
    if (!open || ui.phase !== 'accuracy' || measureStartedRef.current) return;
    measureStartedRef.current = true;
    let cancelled = false;

    (async () => {
      setMeasuring(true);
      updateCalibration({
        status: 'calibrating',
        progress: 95,
        message: 'Keep still — stare at the center dot for 5 seconds.',
        canStartExam: false,
      });
      try {
        // Ensure canvas exists before stare — WebGazer draws then storePoints.
        ensurePlottingCanvas();
        const accuracy = await measureCalibrationAccuracy(ACCURACY_STARE_MS);
        if (cancelled) return;
        setUi((prev) => applyAccuracyResult(prev, accuracy));
        updateCalibration({
          status: 'calibrating',
          progress: 100,
          accuracy,
          message: `Measured accuracy ${accuracy}%. ${
            canStartExam(accuracy)
              ? 'You can continue to the exam.'
              : `Need at least ${MIN_CALIBRATION_ACCURACY}% — please recalibrate.`
          }`,
          canStartExam: false,
        });
      } catch (err) {
        if (cancelled) return;
        clearWebGazerData();
        const message =
          err instanceof Error ? err.message : 'Accuracy measurement failed. Please recalibrate.';
        setLocalError(message);
        setError(message);
        setUi(createInitialCalibrationState());
        measureStartedRef.current = false;
      } finally {
        if (!cancelled) setMeasuring(false);
      }
    })();

    return () => {
      cancelled = true;
      // Allow re-run after Strict Mode remount / effect restart while still in accuracy.
      measureStartedRef.current = false;
    };
  }, [open, ui.phase, updateCalibration, setError]);

  const beginPoints = useCallback(() => {
    if (!started || error) return;
    setCalibrationPreview(true);
    setUi((prev) => ({ ...prev, phase: 'points' }));
    updateCalibration({
      status: 'calibrating',
      progress: 0,
      message: `Click each point ${CLICKS_PER_POINT} times while looking at it.`,
      canStartExam: false,
    });
  }, [started, error, updateCalibration]);

  const handlePointClick = useCallback(
    (pointId: CalibrationPointId) => {
      if (!started || ui.phase !== 'points') return;

      setUi((prev) => {
        const next = recordPointClick(prev, pointId);
        if (shouldStartAccuracy(next)) {
          return { ...next, phase: 'accuracy' };
        }
        return next;
      });
    },
    [started, ui.phase]
  );

  useEffect(() => {
    if (!open || ui.phase !== 'points') return;
    const progress = Math.min(
      90,
      Math.round((ui.completedCount / CALIBRATION_POINTS.length) * 90)
    );
    updateCalibration({
      status: 'calibrating',
      progress,
      samplesCollected: Object.values(ui.clicks).reduce((a, b) => a + b, 0),
      message:
        ui.completedCount >= CALIBRATION_POINTS.length
          ? 'All points done. Stare at the center for accuracy measurement.'
          : `Point clicks: ${ui.completedCount} of ${CALIBRATION_POINTS.length} complete.`,
      canStartExam: false,
    });
  }, [open, ui.phase, ui.completedCount, ui.clicks, updateCalibration]);

  const recalibrate = useCallback(() => {
    clearWebGazerData();
    measureStartedRef.current = false;
    setMeasuring(false);
    setLocalError(null);
    setCalibrationPreview(true);
    setUi(createInitialCalibrationState());
    updateCalibration({
      status: 'calibrating',
      progress: 0,
      accuracy: undefined,
      message: 'Recalibrating — follow the mouse with your eyes, then click Calibrate.',
      canStartExam: false,
    });
  }, [updateCalibration]);

  const handleContinue = useCallback(() => {
    const accuracy = ui.accuracy;
    if (accuracy == null || !canStartExam(accuracy)) return;

    updateCalibration({
      status: 'completed',
      progress: 100,
      canStartExam: true,
      accuracy,
      message: `On-device gaze calibration complete — accuracy ${accuracy}%.`,
    });
    setCalibrationPreview(false);
    clearPlottingCanvas();
    removePlottingCanvas();
    onComplete();
  }, [ui.accuracy, updateCalibration, onComplete]);

  if (!open) return null;

  const showPoints = ui.phase === 'points' || ui.phase === 'accuracy';
  const centerVisible =
    ui.phase === 'accuracy' || ui.phase === 'result' || isCenterVisible(ui);
  const accuracyOk = ui.accuracy != null && canStartExam(ui.accuracy);

  return (
    <div
      className="fixed inset-0 z-[100050] bg-surface-base/90 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="WebGazer gaze calibration"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col gap-3 px-6 py-5">
        <div className="pointer-events-auto flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl">
            <h2 className="font-display text-heading font-normal text-content-primary">
              Gaze calibration
            </h2>
            <p className="mt-2 text-body text-content-secondary">
              Always follow the mouse with your eyes. WebGazer trains on your device — no video is
              sent to the server for gaze.
            </p>
          </div>
          <div className="pointer-events-auto flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setHelpOpen(true)}>
              Help
            </Button>
            {onCancel && (
              <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {starting && (
          <p className="pointer-events-auto text-sm text-content-secondary">
            Starting on-device gaze…
          </p>
        )}
        {error && (
          <div className="pointer-events-auto flex flex-col gap-2">
            <p className="text-sm text-state-critical">{error}</p>
            <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
              Reload and retry
            </Button>
          </div>
        )}
      </div>

      {/* Ready */}
      {started && !error && ui.phase === 'ready' && (
        <div className="absolute inset-0 z-20 flex items-center justify-center px-6">
          <div className="max-w-md rounded-elevated border border-subtle bg-paper/95 p-6 shadow-elevated">
            <h3 className="font-display text-heading-sm font-normal text-content-primary">
              Ready to calibrate
            </h3>
            <p className="mt-3 text-body text-content-secondary">
              You will click each of 9 points {CLICKS_PER_POINT} times, then stare at the center for{' '}
              {ACCURACY_STARE_MS / 1000} seconds. Exam unlock requires at least{' '}
              {MIN_CALIBRATION_ACCURACY}% accuracy.
            </p>
            <p className="mt-2 text-sm text-content-secondary">
              Keep your face in the green box and always follow the mouse with your eyes.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button type="button" onClick={beginPoints}>
                Calibrate
              </Button>
              <Button type="button" variant="secondary" onClick={() => setHelpOpen(true)}>
                Help
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Calibration points */}
      {started && !error && showPoints && (
        <>
          {CALIBRATION_POINTS.map((point) => {
            const isCenter = point.isCenter;
            if (isCenter && !centerVisible) return null;
            if (ui.phase === 'accuracy' && !isCenter) return null;

            const clicks = ui.clicks[point.id];
            const done = ui.completed[point.id];
            const remaining = Math.max(0, CLICKS_PER_POINT - clicks);
            const clickable = ui.phase === 'points' && !done;

            return (
              <button
                key={point.id}
                type="button"
                disabled={!clickable}
                className={
                  done
                    ? 'absolute z-[100060] flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-amber-500 bg-amber-400 text-xs font-ui font-medium text-ink shadow-lg'
                    : 'absolute z-[100060] flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-4 border-state-warning bg-state-warning-soft text-xs font-ui font-medium text-content-primary shadow-lg transition hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-state-warning disabled:cursor-default'
                }
                style={{
                  left: `${point.xPercent}%`,
                  top: `${point.yPercent}%`,
                  opacity: done ? 1 : pointOpacity(clicks),
                }}
                onClick={() => handlePointClick(point.id)}
                aria-label={
                  done
                    ? `Calibration point ${point.id} complete`
                    : `Calibration point ${point.id}, ${remaining} clicks remaining`
                }
              >
                {ui.phase === 'points' && !done ? remaining : null}
              </button>
            );
          })}
        </>
      )}

      {/* Accuracy stare instruction */}
      {started && !error && ui.phase === 'accuracy' && (
        <div className="pointer-events-none absolute inset-x-0 bottom-10 z-20 flex justify-center px-6">
          <p className="rounded-elevated border border-subtle bg-paper/95 px-4 py-3 text-center text-body text-content-primary shadow-elevated">
            {measuring
              ? 'Do not move the mouse. Stare at the middle dot…'
              : 'Starting accuracy measurement…'}
          </p>
        </div>
      )}

      {/* Result */}
      {started && !error && ui.phase === 'result' && ui.accuracy != null && (
        <div className="absolute inset-0 z-20 flex items-center justify-center px-6">
          <div className="max-w-md rounded-elevated border border-subtle bg-paper/95 p-6 shadow-elevated">
            <h3 className="font-display text-heading-sm font-normal text-content-primary">
              Accuracy | {ui.accuracy}%
            </h3>
            <p className="mt-3 text-body text-content-secondary">
              {accuracyOk
                ? `Calibration meets the ${MIN_CALIBRATION_ACCURACY}% requirement. You may continue to the exam.`
                : `Accuracy is below ${MIN_CALIBRATION_ACCURACY}%. Recalibrate before starting the exam.`}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {accuracyOk && (
                <Button type="button" onClick={handleContinue}>
                  Continue to exam
                </Button>
              )}
              <Button type="button" variant={accuracyOk ? 'secondary' : 'primary'} onClick={recalibrate}>
                Recalibrate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Help (inline — above calibration layer) */}
      {helpOpen && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-obsidian/45 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Calibration help"
        >
          <div className="max-w-lg rounded-elevated border border-subtle bg-paper p-6 shadow-elevated">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-heading-sm font-normal text-content-primary">
                How calibration works
              </h3>
              <Button type="button" variant="ghost" size="sm" onClick={() => setHelpOpen(false)}>
                Close
              </Button>
            </div>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-body text-content-secondary">
              <li>
                Keep your face inside the feedback box — it turns from red to green when tracking is
                good.
              </li>
              <li>A gaze prediction dot shows where WebGazer thinks you are looking.</li>
              <li>
                Click each calibration point {CLICKS_PER_POINT} times while looking at it (center
                appears last).
              </li>
              <li>
                After all points, stare at the center dot for {ACCURACY_STARE_MS / 1000} seconds
                without moving the mouse.
              </li>
              <li>Always follow the mouse cursor with your eyes during the whole process.</li>
              <li>
                You need at least {MIN_CALIBRATION_ACCURACY}% measured accuracy to continue to the
                exam.
              </li>
            </ul>
            <div className="mt-5">
              <Button type="button" onClick={() => setHelpOpen(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Call on exam end / unmount when leaving monitoring. */
export async function teardownWebGazerCalibration(): Promise<void> {
  await stopWebGazer();
}
